import {MutableRefObject} from "react";
import * as signalR from "@microsoft/signalr";
import {ServerInfo} from "./ServerInfoContext";
import {BanList, ChatInit, ConsoleInit, MapInfo, PlayerDetails} from "./interfaces";
import {PlayerInventoryResult} from "./players/inventory/interfaces";
import {WhitelistData} from "./whitelist/interfaces";
import {ServerStats} from "./stats/interfaces";
import {GroupData, PermissionGroupInfo, PlayerPermissions} from "./permissions/interfaces";

export function rpc_send(socket: ISocketConnection, method: string, ...args: any[]) {
    socket.connection.current?.send(method, ...args);
}

export async function rpc_call<T>(socket: ISocketConnection, method: string, ...args: any[]): Promise<T | undefined> {
    if (!socket.connection.current) {
        return undefined
    }
    return await socket.connection.current.invoke(method, ...args)
}

export async function rpc_getServerInfo(socket: ISocketConnection){
    return await rpc_call<ServerInfo>(socket, "GetServerInfo")
}

export async function rpc_getWhitelist(socket: ISocketConnection){
    return await rpc_call<WhitelistData>(socket, "GetWhitelist")
}

export async function rpc_getOnlinePlayerList(socket: ISocketConnection){
    return await rpc_call<PlayerDetails[]>(socket, "GetOnlinePlayerList")
}

export async function rpc_getPlayerInventory(socket: ISocketConnection, steamId: string){
    return await rpc_call<PlayerInventoryResult>(socket, "GetPlayerInventory", steamId)
}

export async function rpc_giveItem(socket: ISocketConnection, steamId: string, shortname: string, amount: number, skinId?: string){
    const result = await rpc_call<RpcActionResult>(socket, "GiveItem", steamId, shortname, amount, skinId ?? null)
    return result ?? rpc_notConnected()
}

export async function rpc_getBanList(socket: ISocketConnection, page: number){
    return await rpc_call<BanList>(socket, "GetBanList", page)
}

export async function rpc_requestMapImage(socket: ISocketConnection){
    const result = await rpc_call<RpcActionResult>(socket, "RequestMapImage");
    return result ?? rpc_notConnected();
}

export async function rpc_getMapInfo(socket: ISocketConnection){
    return await rpc_call<MapInfo>(socket, "GetMapInfo")
}

export function rpc_sendConsoleCommand(socket: ISocketConnection, message: string){
    rpc_send(socket, "SendConsoleCommand", message);
}

export async function rpc_consoleInit(socket: ISocketConnection, historyLength: number){
    return await rpc_call<ConsoleInit>(socket, "ConsoleInit", historyLength);
}

export async function rpc_chatInit(socket: ISocketConnection, historyLength: number, steamIdFilter: string, teamIdFilter: number, chatChannelFilter: number){
    return await rpc_call<ChatInit>(socket, "ChatInit", historyLength, steamIdFilter, teamIdFilter, chatChannelFilter);
}

export async function rpc_sendChatMessage(socket: ISocketConnection, message: string, steamId: string){
    return await rpc_call<RpcActionResult>(socket, "SendChatMessage", message, steamId) ?? rpc_notConnected();
}

export async function rpc_kickPlayer(socket: ISocketConnection, steamId: string, reason: string | undefined){
    const result = await rpc_call<RpcActionResult>(socket, "KickPlayer", steamId, reason);
    return result ?? rpc_notConnected();
}

export async function rpc_banPlayer(socket: ISocketConnection, steamId: string, displayName: string, reason: string | undefined, durationSeconds: number, banIp: string | undefined){
    const result = await rpc_call<RpcActionResult>(socket, "BanPlayer", steamId, displayName, reason, durationSeconds, banIp);
    return result ?? rpc_notConnected();
}

export async function rpc_banIp(socket: ISocketConnection, ip: string, reason: string | undefined, durationSeconds: number){
    const result = await rpc_call<RpcActionResult>(socket, "BanIp", ip, reason, durationSeconds);
    return result ?? rpc_notConnected();
}

export async function rpc_unbanPlayer(socket: ISocketConnection, steamId: string){
    const result = await rpc_call<RpcActionResult>(socket, "UnbanPlayer", steamId);
    return result ?? rpc_notConnected();
}

export async function rpc_unbanIp(socket: ISocketConnection, ip: string){
    const result = await rpc_call<RpcActionResult>(socket, "UnbanIp", ip);
    return result ?? rpc_notConnected();
}

export async function rpc_syncBanList(socket: ISocketConnection){
    const result = rpc_call<RpcActionResult>(socket, "SyncBanList")
    return result ?? rpc_notConnected();
}

export async function rpc_updateWhitelistState(socket: ISocketConnection, enabled: boolean){
    const result = await rpc_call<RpcActionResult>(socket, "UpdateWhitelistState", enabled);
    return result ?? rpc_notConnected();
}

export async function rpc_updateWhitelistReason(socket: ISocketConnection, reason: string){
    const result = await rpc_call<RpcActionResult>(socket, "UpdateWhitelistReason", reason);
    return result ?? rpc_notConnected();
}

export async function rpc_updateWhitelistPlayer(socket: ISocketConnection, steamId: string, remove: boolean){
    const result = await rpc_call<RpcActionResult>(socket, "UpdateWhitelistPlayer", steamId, remove);
    return result ?? rpc_notConnected();
}

// --- Permissions Manager (in-game Oxide permissions) ---

export async function rpc_getAllPermissions(socket: ISocketConnection){
    return await rpc_call<string[]>(socket, "GetAllPermissions")
}

export async function rpc_getPermissionGroups(socket: ISocketConnection){
    return await rpc_call<PermissionGroupInfo[]>(socket, "GetPermissionGroups")
}

export async function rpc_getPlayerPermissions(socket: ISocketConnection, steamId: string){
    return await rpc_call<PlayerPermissions>(socket, "GetPlayerPermissions", steamId)
}

export async function rpc_getGroupData(socket: ISocketConnection, group: string){
    return await rpc_call<GroupData>(socket, "GetGroupData", group)
}

export async function rpc_setPlayerPermission(socket: ISocketConnection, steamId: string, permission: string, granted: boolean){
    const result = await rpc_call<RpcActionResult>(socket, "SetPlayerPermission", steamId, permission, granted)
    return result ?? rpc_notConnected()
}

export async function rpc_setGroupPermission(socket: ISocketConnection, group: string, permission: string, granted: boolean){
    const result = await rpc_call<RpcActionResult>(socket, "SetGroupPermission", group, permission, granted)
    return result ?? rpc_notConnected()
}

export async function rpc_setPlayerGroupMembership(socket: ISocketConnection, steamId: string, group: string, member: boolean){
    const result = await rpc_call<RpcActionResult>(socket, "SetPlayerGroupMembership", steamId, group, member)
    return result ?? rpc_notConnected()
}

export function rpc_notConnected(): RpcActionResult{
    return { success: false, message: "not_connected" }
}

export interface RpcActionResult{
    success: boolean
    message: string
}

export interface ISocketConnection{
    connection: MutableRefObject<signalR.HubConnection | undefined>
}